import { NetworkStatus } from '@apollo/client';
import { useReportList } from '@bechtel/client-common';
import { Box, Button, ButtonSize, Heading, Icon, Spinner, SpinnerSize, Text } from '@bechtel/gds';
import { ProjectReport, ReportList, SortOrder } from '@bechtel/types';
import { Trans } from '@lingui/macro';
import { get, map } from 'lodash';
import { useEffect, useState } from 'react';
import { ProjectAdminContainerStyled, ProjectAdminHeaderStyled, ProjectAdminSubContainerStyled } from '../styled';
import { AddNewReport } from './add';
import { ReportItem } from './item';

export const Reports = () => {
  const [showAddReport, setShowAddReport] = useState(false);
  const [loadReportList, setLoadReportList] = useState<boolean>(false);
  const [disableAddReport, setDisableAddReport] = useState<boolean>(false);
  const reportsLimit = 20;
  const [loadReports, { loading, data, error, refetch, networkStatus, called, fetchMore }] = useReportList();

  useEffect(() => {
    loadReports({
      variables: {
        input: {
          searchKeyword: '',
          sortOrder: SortOrder.ASC,
        },
        paging: {
          cursor: '1',
          limit: reportsLimit,
        },
      },
    });
  }, [loadReports, loadReportList]);

  const reportList: ReportList['items'] = get(data, 'reports.items', []);
  const totalPages = Math.ceil(get(data, 'reports.total', 1) / reportsLimit);
  const reportListCursor = get(data, 'reports.cursor', 0);

  const handleScroll = (e) => {
    if (
      e.target.scrollTop + e.target.clientHeight + 200 >= e.target.scrollHeight &&
      reportListCursor <= totalPages &&
      !loading
    ) {
      fetchMore({
        variables: {
          paging: {
            cursor: reportListCursor.toString(),
            limit: reportsLimit,
          },
        },
      });
      if (!showAddReport) {
        setDisableAddReport(false);
      }
    }
  };

  return (
    <ProjectAdminContainerStyled>
      <ProjectAdminHeaderStyled>
        <Text family="graphik" size="600" weight="600" color="white">
          <Trans>Reports</Trans>
        </Text>
      </ProjectAdminHeaderStyled>
      {error && (
        <Box flexDirection={{ sm: 'column' }} alignItems={{ sm: 'center' }} pt={{ sm: '900' }} mt={{ sm: '500' }}>
          <Box mt={{ sm: '700' }} px={{ sm: '600' }}>
            <Text>
              <Trans>There was a problem loading the Reports.</Trans>
            </Text>
            <Text>
              <Trans>Please click the button below to try again.</Trans>
            </Text>
          </Box>
          <Box>
            <Button size={ButtonSize.MEDIUM} onClick={() => refetch()}>
              <Box mr={{ sm: '400' }}>
                <Icon icon="undo" />
              </Box>
              Retry
            </Button>
          </Box>
        </Box>
      )}
      {!error && (
        <ProjectAdminSubContainerStyled mt={{ sm: '450' }} data-testid="module-data-container">
          <Box my={{ sm: '550' }} mx={{ sm: '600' }} flexDirection={{ sm: 'column' }} flexGrow={{ sm: '1' }}>
            <Box justifyContent={{ sm: 'space-between' }} alignItems={{ sm: 'center' }}>
              <Box flexDirection={{ sm: 'column' }}>
                <Heading family="graphik" size="500" weight="600">
                  <Trans>Manage Reports</Trans>
                </Heading>
                <Box mt={{ sm: '400' }}>
                  <Text family="graphik" size="300">
                    <Trans>Add, Manage Reports</Trans>
                  </Text>
                </Box>
              </Box>
              <Button
                disabled={disableAddReport}
                css={{ width: '150px' }}
                size={ButtonSize.LARGE}
                onClick={() => {
                  setShowAddReport(true);
                  setDisableAddReport(true);
                }}
              >
                <Trans>Add Report</Trans>
              </Button>
            </Box>
            {showAddReport && (
              <AddNewReport
                close={() => {
                  setShowAddReport(false);
                  setDisableAddReport(false);
                }}
                setLoadReportList={setLoadReportList}
              />
            )}
            <Box
              flexDirection={{ sm: 'column' }}
              flexGrow={{ sm: '1' }}
              style={{ overflowY: 'auto' }}
              onScroll={(e) => {
                handleScroll(e);
              }}
            >
              {reportList.length > 0 &&
                !loading &&
                map(reportList, (reportItem: ProjectReport) => {
                  return (
                    <ReportItem
                      key={reportItem.id}
                      reportItem={reportItem}
                      setLoadReportList={setLoadReportList}
                      setDisableAddReport={setDisableAddReport}
                      disableEditReport={disableAddReport}
                    />
                  );
                })}
              {!error && (!called || networkStatus === NetworkStatus.refetch || loading) && (
                <Box
                  flexGrow={{ sm: 1 }}
                  alignItems={{ sm: 'center' }}
                  flexDirection={{ sm: 'column' }}
                  justifyContent={{ sm: 'center' }}
                  data-testid="loading"
                  css={reportList.length === 0 ? 'height: 100%;' : ''}
                >
                  <Spinner size={SpinnerSize.LARGE} />
                </Box>
              )}
            </Box>
          </Box>
        </ProjectAdminSubContainerStyled>
      )}
    </ProjectAdminContainerStyled>
  );
};
